import React, { useState, useEffect } from 'react';
import { Container, Table, Badge, Nav } from 'react-bootstrap';
import { format, isWithinInterval, subDays } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import api from '../../services/api';
import './BookingHistory.css';

const BookingHistory = () => {
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // 格式化日期
  const formatDate = (dateString) => {
    return format(new Date(dateString), 'yyyy年MM月dd日', { locale: zhTW });
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 過濾預約記錄
  const getFilteredBookings = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (activeTab) {
      case 'history':
        return bookings.filter(booking => new Date(booking.date) < today);
      case 'pending':
        return bookings.filter(booking => 
          booking.status === 'PENDING' && new Date(booking.date) >= today
        );
      case 'accepted':
        return bookings.filter(booking => 
          booking.status === 'ACCEPTED' && new Date(booking.date) >= today
        );
      case 'denied':
        return bookings.filter(booking => 
          booking.status === 'DENIED' && new Date(booking.date) >= today
        );
      default:
        return bookings.filter(booking => new Date(booking.date) >= today);
    }
  };

  // 按預約編號分組顯示
  const renderBookingGroups = () => {
    const filteredBookings = getFilteredBookings();
    
    // 分組邏輯
    const groupedBookings = filteredBookings.reduce((acc, booking) => {
      if (!acc[booking.booking_no]) {
        acc[booking.booking_no] = [];
      }
      acc[booking.booking_no].push(booking);
      return acc;
    }, {});

    if(Object.keys(groupedBookings).length === 0) {
      if(activeTab === 'history') {
        return <h5 className="mt-4">沒有歷史預約記錄</h5>;
      }
      return <h5 className="mt-4">沒有進行中的預約記錄</h5>;
    }

    return (
      <div className="booking-groups">
        {Object.entries(groupedBookings).map(([bookingNo, groupBookings]) => (
          <div key={bookingNo} className="booking-group mb-4">
            <Table striped bordered hover responsive>
              {isMobileView ? (
                // Mobile View
                <thead>
                  <tr className="table-primary">
                    <th colSpan="10">預約編號: {bookingNo}</th>
                  </tr>
                  <tr>
                    <th colSpan="10">申請日期: {formatDate(groupBookings[0].created_at)}</th>
                  </tr>
                  <tr>
                      <th colSpan="6">期望上課地點: {groupBookings[0].preferred_location}</th>
                      <th colSpan="4">人數: {groupBookings[0].numberof_students}</th>
                  </tr>
                  {groupBookings[0].note && (
                    <tr>
                      <th colSpan="10">備註: {groupBookings[0].note}</th>
                    </tr>
                  )}
                </thead>
              ) : (
                // Desktop View
                <thead>
                  <tr className="table-primary">
                    <th colSpan="6">預約編號: {bookingNo}</th>
                  </tr>
                  <tr>
                    <th colSpan="2">期望上課地點: {groupBookings[0].preferred_location}</th>
                    <th colSpan="2">人數: {groupBookings[0].numberof_students}</th>
                    <th colSpan="2">申請日期: {formatDate(groupBookings[0].created_at)}</th>
                  </tr>
                  {groupBookings[0].note && (
                    <tr>
                      <th colSpan="6">備註: {groupBookings[0].note}</th>
                    </tr>
                  )}
                  <tr>
                    <th>課程日期</th>
                    <th>時段</th>
                    <th colSpan="2">教練</th>
                    <th colSpan="1">課程類型</th>
                    <th colSpan="1">狀態</th>
                  </tr>
                </thead>
              )}
              {isMobileView ? (
                <tbody>
                  {groupBookings.map((booking) => (
                    <tr key={booking.id} className="mobile-booking-row">
                      <td colSpan="10">
                        <div className="mobile-booking-card">
                          <div className="d-flex justify-content-between align-items-start w-100">
                            <div>
                              <div className="booking-detail">
                                <span className="detail-label">時間：</span>
                                <span className="detail-value">
                                  {formatDate(booking.date)}
                                </span>
                                <span className="time-slot-display mx-2">
                                  <Badge bg="primary" className="time-badge">
                                    {booking.time_slot_display}
                                  </Badge>
                                </span>
                              </div>
                              <div className="booking-detail">
                                <span className="detail-label">教練：</span>
                                <span className="detail-value">{booking.coach_name}</span>
                              </div>
                              <div className="booking-detail">
                                <span className="detail-label">類型：</span>
                                <span className="detail-value">{booking.type_display}</span>
                              </div>
                            </div>
                            <div>
                              {booking.status === 'PENDING' && (
                                <Badge bg="warning" className="status-badge">
                                  等待教練確認
                                </Badge>
                              )}
                              {booking.status === 'ACCEPTED' && (
                                <Badge bg="success" className="status-badge">
                                  已確認
                                </Badge>
                              )}
                              {booking.status === 'DENIED' && (
                                <Badge bg="danger" className="status-badge">
                                  已拒絕
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              ) : (
                <tbody>
                {groupBookings.map((booking) => (
                <tr key={booking.id}>
                  <td>{formatDate(booking.date)}</td>
                  <td>{booking.time_slot_display}</td>
                  <td colSpan="2">{booking.coach_name}</td>
                  <td colSpan="1">{booking.type_display}</td>
                  <td colSpan="1">
                    {booking.status === 'PENDING' && (
                      <Badge bg="warning" className="status-badge">
                        等待教練確認
                      </Badge>
                    )}
                    {booking.status === 'ACCEPTED' && (
                      <Badge bg="success" className="status-badge">
                        已確認
                      </Badge>
                    )}
                    {booking.status === 'DENIED' && (
                      <Badge bg="danger" className="status-badge">
                        已拒絕
                      </Badge>
                    )}
                  </td>
                </tr>
                ))}
                </tbody>
              )}
            </Table>
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await api.get('/bookings/');
        const sortedBookings = response.data.sort((a, b) => {
          const dateComparison = new Date(a.date) - new Date(b.date);
          if (dateComparison !== 0) return dateComparison;
          return a.time_slot === 'MORNING' ? -1 : 1;
        });
        setBookings(sortedBookings);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      }
    };

    fetchBookings();
  }, []);

  return (
    <Container className="py-4">
      <div className="booking-tabs">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0 fw-bold">進行中預約</h5>
          <h5 className="mb-0 fw-bold">歷史預約</h5>
        </div>
        <div className="d-flex gap-4">
          <div className="flex-grow-1">
            <Nav variant="tabs" className="mb-4">
              <Nav.Item>
                <Nav.Link 
                  active={activeTab === 'all'}
                  onClick={() => setActiveTab('all')}
                  className={activeTab === 'all' ? 'active' : ''}
                >
                  全部
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  active={activeTab === 'accepted'}
                  onClick={() => setActiveTab('accepted')}
                  className={activeTab === 'accepted' ? 'active' : ''}
                >
                  已確認
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  active={activeTab === 'pending'}
                  onClick={() => setActiveTab('pending')}
                  className={activeTab === 'pending' ? 'active' : ''}
                >
                  待確認
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  active={activeTab === 'denied'}
                  onClick={() => setActiveTab('denied')}
                  className={activeTab === 'denied' ? 'active' : ''}
                >
                  已拒絕
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </div>
          
          <div>
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link 
                  active={activeTab === 'history'}
                  onClick={() => setActiveTab('history')}
                  className={activeTab === 'history' ? 'active' : ''}
                >
                  全部
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </div>
        </div>
      </div>
      {renderBookingGroups()}
    </Container>
  );
};

export default BookingHistory; 